import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, Button, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import logo from '../../assets/VisibeenLogo.png';
import InstagramIcon from '../icons/InstagramFooterIcon';
import FacebookIcon from '../icons/FacebookIcon';
import LinkedInIcon from '../icons/LinkedInIcon';
import YouTubeIcon from '../icons/YouTubeFooterIcon';

const FooterContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
  padding: '40px 0 24px 0',
  color: '#111827',
  fontFamily: 'Inter, sans-serif',
  borderTop: '1px solid #E2E8F0',
  boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.down('md')]: {
    padding: '32px 0 20px 0'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px 0 16px 0'
  }
}));

const FooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 40px',
  gap: '60px',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    padding: '0 24px',
    gap: '32px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    gap: '24px'
  }
}));

const FooterLeft = styled(Stack)(() => ({
  gap: '24px',
  alignItems: 'flex-start'
}));

const FooterLogo = styled('img')(() => ({
  width: '120px',
  height: 'auto'
}));

const CopyrightText = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 400,
  color: '#6B7280',
  lineHeight: '20px'
}));

const SocialIcons = styled(Stack)(() => ({
  flexDirection: 'row',
  gap: '12px'
}));

const SocialIconLink = styled(Link)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '8px',
  backgroundColor: '#F3F4F6',
  color: '#6B7280',
  textDecoration: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#0EA5E9',
    color: '#ffffff'
  }
}));

const FooterColumns = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: '48px',
  alignItems: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '24px',
    width: '100%'
  }
}));

const FooterColumn = styled(Stack)(() => ({
  gap: '16px',
  minWidth: '120px'
}));

const ColumnTitle = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827'
}));

const ColumnLinks = styled(Stack)(() => ({
  gap: '10px'
}));

const ColumnLink = styled(Link)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#6B7280',
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  '&:hover': {
    color: '#0EA5E9'
  }
}));

const NewsletterContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  minWidth: '260px',
  [theme.breakpoints.down('md')]: {
    minWidth: '100%',
    width: '100%'
  }
}));

const NewsletterDescription = styled(Typography)(() => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: '#6B7280',
  lineHeight: '20px'
}));

const NewsletterForm = styled(Box)(() => ({
  position: 'relative',
  width: '100%'
}));

const NewsletterInput = styled('input')(({ theme }) => ({
  width: '100%',
  height: '42px',
  padding: '10px 48px 10px 14px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#111827',
  backgroundColor: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  outline: 'none',
  transition: 'all 0.2s ease',
  '&::placeholder': {
    color: '#9CA3AF',
    fontSize: '14px'
  },
  '&:focus': {
    backgroundColor: '#FFFFFF',
    borderColor: '#0EA5E9',
    boxShadow: '0 0 0 3px rgba(14, 165, 233, 0.1)'
  },
  '&:hover': {
    borderColor: '#D1D5DB'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    height: '40px'
  }
}));

const SendButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  right: '4px',
  top: '50%',
  transform: 'translateY(-50%)',
  minWidth: '34px',
  width: '34px',
  height: '34px',
  padding: 0,
  backgroundColor: '#0EA5E9',
  color: '#ffffff',
  fontSize: '16px',
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#0284C7'
  },
  [theme.breakpoints.down('sm')]: {
    width: '32px',
    height: '32px',
    minWidth: '32px'
  }
}));

const DashboardFooter = () => {
  const navigate = useNavigate();

  const handleQuickTutorial = (event) => {
    event.preventDefault();
    navigate('/client-onboarding');
  };

  return (
    <FooterContainer>
      <FooterContent>
        <FooterLeft>
          <FooterLogo src={logo} alt="Visibeen" />
          <Stack spacing={1}>
            <CopyrightText>Copyright © 2025 e2e digitech pvt ltd.</CopyrightText>
            <CopyrightText>All rights reserved</CopyrightText>
          </Stack>
          <SocialIcons>
            <SocialIconLink href="https://www.instagram.com/visibeen/" target="_blank" rel="noopener noreferrer">
              <InstagramIcon width={18} height={18} color="currentColor" />
            </SocialIconLink>
            <SocialIconLink href="https://www.facebook.com/profile.php?id=61566867428338" target="_blank" rel="noopener noreferrer">
              <FacebookIcon width={18} height={18} color="currentColor" />
            </SocialIconLink>
            <SocialIconLink href="https://www.youtube.com/@Visibeen" target="_blank" rel="noopener noreferrer">
              <YouTubeIcon width={18} height={18} color="currentColor" />
            </SocialIconLink>
            <SocialIconLink href="https://www.linkedin.com/company/visibeen/" target="_blank" rel="noopener noreferrer">
              <LinkedInIcon width={18} height={18} color="currentColor" />
            </SocialIconLink>
          </SocialIcons>
        </FooterLeft>

        <FooterColumns>
          <FooterColumn>
            <ColumnTitle>Company</ColumnTitle>
            <ColumnLinks>
              <ColumnLink href="/about-us">About us</ColumnLink>
              <ColumnLink href="#" onClick={handleQuickTutorial} style={{ cursor: 'pointer' }}>
                Quick Tutorial
              </ColumnLink>
              <ColumnLink href="/pricing">Pricing</ColumnLink>
            </ColumnLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Support</ColumnTitle>
            <ColumnLinks>
              <ColumnLink href="mailto:support@visibeen.com">support@visibeen.com</ColumnLink>
              <ColumnLink href="/contact-us">Help center</ColumnLink>
              <ColumnLink href="/terms-of-service">Terms of service</ColumnLink>
              <ColumnLink href="/privacy-policy">Privacy policy</ColumnLink>
              <ColumnLink href="/disclaimer">Disclaimer</ColumnLink>
            </ColumnLinks>
          </FooterColumn>

          <NewsletterContainer>
            <ColumnTitle>Stay up to date</ColumnTitle>
            <NewsletterDescription>
              Subscribe to our newsletter for the latest updates and exclusive offers.
            </NewsletterDescription>
            <NewsletterForm>
              <NewsletterInput
                type="email"
                placeholder="Enter your email address"
                aria-label="Email address"
              />
              <SendButton aria-label="Subscribe">
                →
              </SendButton>
            </NewsletterForm>
          </NewsletterContainer>
        </FooterColumns>
      </FooterContent>
    </FooterContainer>
  );
};

export default DashboardFooter;
